import type { ReactElement, ReactNode } from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { TbTrendingDown, TbTrendingUp } from 'react-icons/tb';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ReportingComparison } from '../../../types/reporting';
import {
    formatChartValue,
    formatPercent,
    formatTooltipValue,
    PIE_MAX_NAMED_SLICES,
    PIE_SHOW_ALL_NAMED_UP_TO,
    type MetricFormat,
} from './formatters';
import { calculateMetricChange } from './metricChange';
import { getTimeseriesComparisonLabel } from './reportingComparisonLabels';
import { PieChartLegend } from './PieChartLegend';

/** Hide on-slice labels below this Recharts percent (0–1). */
export const DONUT_LABEL_MIN_PERCENT = 0.05;
/** Fullscreen donuts have more room — show labels down to 3%. */
export const DONUT_LABEL_MIN_PERCENT_FULLSCREEN = 0.03;

const DEFAULT_DONUT_VALUE_FONT = 11;

export function donutLabelMinPercent(isFullscreen: boolean): number {
    return isFullscreen ? DONUT_LABEL_MIN_PERCENT_FULLSCREEN : DONUT_LABEL_MIN_PERCENT;
}

export type ReportingDonutSlice = {
    name: string;
    value: number;
    color: string;
    /** Absolute/signed value for on-slice labels and tooltips (defaults to `value`). */
    rawValue?: number;
    /** Prior-period total for the same slice when comparison is on. */
    priorRawValue?: number;
    clickable?: boolean;
    /** Chart drill key, for example `flag:amount` or `billable:postedOpen`. */
    seriesKey?: string;
};

/** 1–8 names stay; 9+ become 7 + Other. `padTo100` adds leftover org % (Overview only). */
export function foldDonutOther<T extends { value: number }>(
    items: T[],
    buildOther: (tail: T[], otherValue: number) => T,
    options?: { padTo100?: boolean },
): T[] {
    const namedCount = items.length <= PIE_SHOW_ALL_NAMED_UP_TO
        ? items.length
        : PIE_MAX_NAMED_SLICES;
    const named = items.slice(0, namedCount);
    const tail = items.slice(namedCount);
    const tailValue = tail.reduce((sum, item) => sum + item.value, 0);
    const namedTotal = named.reduce((sum, item) => sum + item.value, 0);
    const rawShown = namedTotal + tailValue;
    let otherValue = tailValue;
    if (options?.padTo100 && rawShown < 99.9) {
        otherValue += 100 - rawShown;
    }
    const folded = otherValue > 0 ? [...named, buildOther(tail, otherValue)] : named;
    if (options?.padTo100 && rawShown > 100.1) {
        return folded.map((item) => ({ ...item, value: (item.value / rawShown) * 100 }));
    }
    return folded;
}

export function donutMidLabelPoint(
    cx: number,
    cy: number,
    midAngle: number,
    innerRadius: number,
    outerRadius: number,
): { x: number; y: number } {
    const rad = (midAngle * Math.PI) / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    return {
        x: cx + radius * Math.cos(-rad),
        y: cy + radius * Math.sin(-rad),
    };
}

/**
 * On-slice value annotation (Invoicing / Inventory donut UX).
 * Reads `payload.rawValue` when present, otherwise `payload.value`.
 * `asShare` prints the slice's share of the doughnut instead of that value.
 */
export function renderDonutValueLabel(
    format: MetricFormat,
    options?: { fontSize?: number; isFullscreen?: boolean; asShare?: boolean },
) {
    const fontSize = options?.fontSize ?? DEFAULT_DONUT_VALUE_FONT;
    const isFullscreen = options?.isFullscreen ?? false;
    const asShare = options?.asShare ?? false;
    return ({ cx, cy, midAngle, innerRadius, outerRadius, percent, payload }: any) => {
        const raw = payload?.rawValue ?? payload?.value ?? 0;
        if (!raw || percent < donutLabelMinPercent(isFullscreen)) return null;
        const { x, y } = donutMidLabelPoint(cx, cy, midAngle, innerRadius, outerRadius);
        const label = asShare
            ? formatPercent((percent ?? 0) * 100)
            : formatChartValue(raw, format);
        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={fontSize}
                fontWeight="bold"
                pointerEvents="none"
            >
                {label}
            </text>
        );
    };
}

/**
 * Hover content: category name + metric label + formatted value,
 * plus prior / % change when comparison data is present (Invoicing-style).
 */
export function DonutSliceTooltip({
    active,
    payload,
    metricLabel,
    format,
    comparison,
    preset,
    granularity,
}: {
    active?: boolean;
    payload?: Array<{
        name?: string;
        color?: string;
        value?: number;
        payload?: {
            name?: string;
            color?: string;
            rawValue?: number;
            priorRawValue?: number;
            value?: number;
        };
    }>;
    metricLabel: string;
    format: MetricFormat;
    comparison?: ReportingComparison;
    preset?: string;
    granularity?: string;
}) {
    if (!active || !payload?.length) return null;
    const entry = payload[0];
    const item = entry.payload ?? {};
    const label = String(item.name ?? entry.name ?? '');
    const color = String(item.color ?? entry.color ?? 'brand.solid');
    const value = Number(item.rawValue ?? item.value ?? entry.value ?? 0);
    const priorValue = comparison && typeof item.priorRawValue === 'number'
        ? item.priorRawValue
        : undefined;
    const change = calculateMetricChange(value, priorValue, false);
    const comparisonLabel = getTimeseriesComparisonLabel(preset, granularity) ?? 'vs prior';
    const isPositive = change !== undefined && change >= 0;

    return (
        <Box
            bg="bg.surface"
            p={3}
            border="1px solid"
            borderColor="border.default"
            borderRadius="lg"
            boxShadow="none"
            minW="150px"
        >
            <Flex align="center" gap={2} mb={1}>
                <Box w={2} h={2} bg={color} borderRadius="sm" />
                <Text fontSize="sm" fontWeight="semibold" color="fg.default">{label}</Text>
            </Flex>
            <Text fontSize="xs" color="fg.muted" mb={1}>{metricLabel}</Text>
            <Text fontSize="lg" fontWeight="bold" color="brand.solid" mb={1}>
                {formatTooltipValue(value, format)}
            </Text>
            {priorValue !== undefined && (
                <Text fontSize="xs" color="fg.muted" mb={1}>
                    Prior: {formatTooltipValue(priorValue, format)}
                </Text>
            )}
            {change !== undefined && (
                <Flex align="center" gap={1}>
                    <Icon
                        as={(isPositive ? TbTrendingUp : TbTrendingDown) as React.ElementType}
                        color={isPositive ? 'green.500' : 'red.500'}
                        boxSize={3}
                    />
                    <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color={isPositive ? 'green.500' : 'red.500'}
                    >
                        {Math.abs(change).toFixed(1)}%
                        <Text as="span" color="fg.muted" ml={1} fontWeight="normal">
                            {comparisonLabel}
                        </Text>
                    </Text>
                </Flex>
            )}
            {comparison && priorValue === undefined && (
                <Text fontSize="xs" color="fg.muted">—</Text>
            )}
        </Box>
    );
}

/**
 * Square donut + side legend.
 * Pass `valueFormat` + `metricLabel` for the shared value-on-slice + category hover UX,
 * or supply custom `label` / `tooltip` (e.g. Invoicing comparison tooltips).
 */
export function ReportingDonut({
    slices,
    isFullscreen,
    label,
    tooltip,
    onSliceClick,
    valueFormat,
    metricLabel,
    labelAsShare,
    labelFontSize,
    comparison,
    preset,
    granularity,
}: {
    slices: ReportingDonutSlice[];
    isFullscreen: boolean;
    label?: (props: any) => ReactNode;
    tooltip?: ReactElement | null;
    onSliceClick?: (slice: ReportingDonutSlice, index: number) => void;
    /** When set (and `label` omitted), annotate slices with formatted values. */
    valueFormat?: MetricFormat;
    /** When set with `valueFormat` (and `tooltip` omitted), show category hover tooltip. */
    metricLabel?: string;
    /** On-slice labels show the slice's share. Tooltips still use `valueFormat`. */
    labelAsShare?: boolean;
    labelFontSize?: number;
    comparison?: ReportingComparison;
    preset?: string;
    granularity?: string;
}) {
    const resolvedLabel =
        label
        ?? (valueFormat
            ? renderDonutValueLabel(valueFormat, {
                fontSize: labelFontSize,
                isFullscreen,
                asShare: labelAsShare,
            })
            : undefined);

    const resolvedTooltip =
        tooltip
        ?? (valueFormat && metricLabel
            ? (
                <Tooltip
                    content={(props: any) => (
                        <DonutSliceTooltip
                            {...props}
                            metricLabel={metricLabel}
                            format={valueFormat}
                            comparison={comparison}
                            preset={preset}
                            granularity={granularity}
                        />
                    )}
                />
            )
            : undefined);

    return (
        <Flex h="100%" w="100%" minH={0} align="center" justify="center" gap={8}>
            <Box h="100%" maxH="100%" aspectRatio={1} flexShrink={0} minW={0} maxW="70%">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={slices}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="50%"
                            outerRadius="90%"
                            paddingAngle={2}
                            minAngle={2}
                            stroke="none"
                            labelLine={false}
                            label={resolvedLabel}
                            onClick={onSliceClick
                                ? (_, index) => {
                                    const slice = typeof index === 'number' ? slices[index] : undefined;
                                    if (!slice?.clickable) return;
                                    onSliceClick(slice, index);
                                }
                                : undefined}
                        >
                            {slices.map((slice, i) => (
                                <Cell
                                    key={`${slice.name}-${i}`}
                                    fill={slice.color}
                                    cursor={slice.clickable ? 'pointer' : undefined}
                                />
                            ))}
                        </Pie>
                        {resolvedTooltip}
                    </PieChart>
                </ResponsiveContainer>
            </Box>
            <PieChartLegend
                items={slices.map((slice) => ({ name: slice.name, color: slice.color }))}
                isFullscreen={isFullscreen}
            />
        </Flex>
    );
}
